import React, { useState } from 'react';
import MarketingLayout from '../components/MarketingLayout';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import '../marketing.css';

const CompaniesPage = () => {
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.target);
    const companyName = formData.get('company_name');
    const email = formData.get('email');
    const contactName = formData.get('contact_name');
    const role = formData.get('role');

    try {
      await axios.post(`${API}/cta/submit`, {
        submission_type: 'company',
        name: companyName,
        email: email,
        contact_name: contactName,
        role: role
      });

      toast.success("Thank you! We'll be in touch soon.");
      e.target.reset();
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error('CTA submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MarketingLayout>
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              Turn Your ESG Budget into <span className="highlight">Verifiable Community Impact</span>
            </h1>
            <p className="hero-subtitle">
              PlayBack Token connects your corporate social responsibility investment directly to real volunteer hours in local sports and community clubs—with complete transparency and independently verifiable proof of impact.
            </p>
            <div className="hero-buttons">
              <a href="#contact" className="btn btn-large btn-primary">Talk to Us</a>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="value-prop">
        <div className="container">
          <h2 className="section-title">PlayBack Token Makes Every Euro's Impact Visible</h2>
          <p className="section-subtitle">
            PlayBack Token operates as a verified marketplace that creates direct, traceable connections between your ESG budget and authentic volunteer hours in local sports and community organisations. Every investment you make generates transparent, independently verifiable proof of real community impact—transforming CSR from aspirational claims into demonstrable results.
          </p>
          
          <div className="value-grid">
            <div className="value-card">
              <div className="value-icon value-icon-image">
                <div className="phone-with-screen">
                  <img src="https://images.unsplash.com/photo-1691256676376-357c3aa66c89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxoYW5kJTIwc21hcnRwaG9uZXxlbnwwfHx8fDE3NjIyNjIwNTV8MA&ixlib=rb-4.1.0&q=85&w=400" alt="Hand holding smartphone" className="value-phone-image" />
                  <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 400'%3E%3Crect fill='%23dc2626' width='200' height='60'/%3E%3Ctext x='100' y='35' font-family='Arial' font-size='16' fill='white' text-anchor='middle'%3ELog Your Hours%3C/text%3E%3Crect fill='white' y='60' width='200' height='340'/%3E%3Ctext x='20' y='90' font-family='Arial' font-size='10' fill='%23666'%3EDate%3C/text%3E%3Crect fill='%23f3f4f6' x='20' y='95' width='160' height='25' rx='4'/%3E%3Ctext x='20' y='135' font-family='Arial' font-size='10' fill='%23666'%3EStart Time%3C/text%3E%3Crect fill='%23f3f4f6' x='20' y='140' width='160' height='25' rx='4'/%3E%3Ctext x='20' y='180' font-family='Arial' font-size='10' fill='%23666'%3EHours%3C/text%3E%3Crect fill='%23f3f4f6' x='20' y='185' width='160' height='25' rx='4'/%3E%3Ctext x='20' y='225' font-family='Arial' font-size='10' fill='%23666'%3EActivity Type%3C/text%3E%3Crect fill='%23f3f4f6' x='20' y='230' width='160' height='25' rx='4'/%3E%3Ctext x='20' y='270' font-family='Arial' font-size='10' fill='%23666'%3EDescription%3C/text%3E%3Crect fill='%23f3f4f6' x='20' y='275' width='160' height='60' rx='4'/%3E%3Crect fill='%23dc2626' x='50' y='355' width='100' height='30' rx='6'/%3E%3Ctext x='100' y='375' font-family='Arial' font-size='12' fill='white' text-anchor='middle'%3ESubmit%3C/text%3E%3C/svg%3E" alt="App screen" className="phone-screen-overlay" />
                </div>
              </div>
              <h3>Verified Hours</h3>
              <p>Every volunteer hour authenticated and tracked through our secure platform</p>
            </div>
            
            <div className="value-card">
              <div className="value-icon value-icon-image">
                <img src="https://customer-assets.emergentagent.com/job_volunteer-hub-35/artifacts/5ta1s578_playback%20logo.jpg" alt="PlayBack Token" className="value-logo-image" />
              </div>
              <h3>Verified Impact</h3>
              <p>Each verified hour becomes a unique, traceable PlayBack Token you can purchase</p>
            </div>
            
            <div className="value-card">
              <div className="value-icon value-icon-image">
                <img src="https://customer-assets.emergentagent.com/job_impact-tokens/artifacts/0hoblwul_ChatGPT-Image-May-28-2025-03_26_56-PM-768x512.png" alt="Corporate partnership" className="value-partnership-image" />
              </div>
              <h3>Direct Funding</h3>
              <p>Your investment flows straight to grassroots organisations</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <h2 className="section-title">How the PlayBack Token Platform Works</h2>
          
          <div className="steps-detailed">
            <div className="step-detailed">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3>Volunteer Time Tracking</h3>
                <p>Sports clubs and community organisations log volunteer hours through our secure platform. Every single hour is authenticated, verified, and tracked—ensuring you receive reliable, audit-ready data for your impact reporting and stakeholder communications.</p>
              </div>
            </div>
            
            <div className="step-detailed">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3>Tokenization of Community Hours</h3>
                <p>Each verified volunteer hour is converted into a unique PlayBack Token. This innovative process creates a clear, fully traceable connection from community action to your corporate investment—perfect for demonstrating genuine, measurable social impact to boards and investors.</p>
              </div>
            </div>
            
            <div className="step-detailed">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3>Corporate ESG/CSR Investment</h3>
                <p>Purchase tokens as an integral component of your social impact strategy. Your investment flows directly to grassroots organisations with complete transparency and no administrative overhead eating into community funding. Every euro supports verified volunteer work.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="why-choose">
        <div className="container">
          <h2 className="section-title">Why choose PlayBack Token?</h2>
          
          <div className="benefits-grid-large">
            <div className="benefit-card-large">
              <h3>Complete Transparency</h3>
              <p>Every single token is traceable back to a verified volunteer hour with documented proof. No more uncertainty about where your investment goes or what tangible outcomes it achieves in communities.</p>
            </div>
            
            <div className="benefit-card-large">
              <h3>Measurable Impact</h3>
              <p>Report exact volunteer hours supported, specific communities reached, and precise funding distributed. Transform your social impact narrative from aspirational stories into hard numbers that satisfy stakeholders.</p>
            </div>
            
            <div className="benefit-card-large">
              <h3>Authentic Community Connection</h3>
              <p>Support real volunteers doing meaningful work in local communities. This genuine engagement resonates powerfully with employees, investors, customers, and regulatory bodies who scrutinize corporate commitments.</p>
            </div>
            
            <div className="benefit-card-large">
              <h3>Independently Verified Proof</h3>
              <p>Verified records mean you can confidently demonstrate your social impact to any audience. Every claim you make can be independently checked and validated by third parties.</p>
            </div>
            
            <div className="benefit-card-large">
              <h3>Fits Your Reporting Framework</h3>
              <p>Seamlessly integrates with major ESG frameworks including GRI Standards, UN SDGs, and EU CSRD requirements, making your compliance reporting significantly easier and more credible.</p>
            </div>
            
            <div className="benefit-card-large">
              <h3>Strategic Geographic Impact</h3>
              <p>Support clubs in regions where you operate facilities or where your employees live and work. Create meaningful local connections that genuinely matter to your organisation's stakeholders.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ESG Applications */}
      <section className="esg-applications">
        <div className="container">
          <h2 className="section-title">ESG Applications Across Industries</h2>
          
          <div className="industry-grid">
            <div className="industry-card">
              <h3>Financial Services & Banking</h3>
              <p>Meet increasing regulatory requirements for documented social investment while supporting financial literacy programs and youth sports initiatives in underserved communities where your branches operate.</p>
            </div>
            
            <div className="industry-card">
              <h3>Technology & Professional Services</h3>
              <p>Engage employees with meaningful local impact that aligns with your innovation values. Perfect for companies actively seeking B Corp certification or working to improve their Glassdoor ESG ratings.</p>
            </div>
            
            <div className="industry-card">
              <h3>Manufacturing & Industrial</h3>
              <p>Strengthen your social license to operate by demonstrably supporting community sports infrastructure where your facilities and employees are located, building lasting goodwill.</p>
            </div>
            
            <div className="industry-card">
              <h3>Retail & Consumer Brands</h3>
              <p>Demonstrate authentic community commitment to increasingly conscious consumers who make purchasing decisions based on verified social impact credentials and corporate responsibility.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="comparison-section">
        <div className="container">
          <h2 className="section-title">A Better Way to Create Measurable Social Impact</h2>
          
          <div className="comparison-table">
            <div className="comparison-column traditional">
              <h3>Traditional CSR Challenges</h3>
              <ul>
                <li>Unclear destination for funding with multiple intermediaries</li>
                <li>Administrative costs significantly reduce community impact</li>
                <li>Impossible to prove concrete outcomes to stakeholders</li>
                <li>One-size-fits-all packages that don't align with your values</li>
                <li>Persistent questions about authenticity from employees and investors</li>
              </ul>
            </div>
            
            <div className="comparison-column playback">
              <h3>PlayBack Token Advantages</h3>
              <ul>
                <li>Complete transparency with full fund tracking visibility</li>
                <li>100% direct funding flows to community organisations</li>
                <li>Every volunteer hour verified and independently tracked</li>
                <li>Choose specific clubs, regions, and impact areas strategically</li>
                <li>Independently verifiable impact that satisfies any audit</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ESG Reporting Frameworks */}
      <section className="esg-frameworks">
        <div className="container">
          <h2 className="section-title">Makes ESG Reporting Easier and More Credible</h2>
          
          <div className="frameworks-grid">
            <div className="framework-card">
              <h3>GRI Standards Compliance</h3>
              <p>Report on local community engagement with verified volunteer hours and direct funding amounts that meet rigorous GRI disclosure requirements for social impact.</p>
            </div>
            
            <div className="framework-card">
              <h3>UN Sustainable Development Goals</h3>
              <p>Demonstrate measurable contribution to SDG 3 (Good Health), SDG 11 (Sustainable Communities), and SDG 17 (Partnerships) with concrete numbers and verified outcomes.</p>
            </div>
            
            <div className="framework-card">
              <h3>EU CSRD Requirements</h3>
              <p>Meet Corporate Sustainability Reporting Directive requirements with detailed social impact data, independent verification, and double materiality assessment support.</p>
            </div>
            
            <div className="framework-card">
              <h3>Other Major Frameworks</h3>
              <p>Fully compatible with SASB, TCFD, and other major ESG frameworks your organisation may be using for investor relations and regulatory compliance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Built for Transparency */}
      <section className="transparency-section">
        <div className="container">
          <h2 className="section-title">Built for Transparency and Compliance</h2>
          
          <div className="transparency-grid">
            <div className="transparency-card">
              <div className="transparency-icon">✓</div>
              <p>Verified records provide clear, auditable proof of impact</p>
            </div>
            
            <div className="transparency-card">
              <div className="transparency-icon">🔒</div>
              <p>Fully compliant with GDPR and data privacy regulations</p>
            </div>
            
            <div className="transparency-card">
              <div className="transparency-icon">💰</div>
              <p>Transparent pricing with direct community funding guarantee</p>
            </div>
            
            <div className="transparency-card">
              <div className="transparency-icon">📊</div>
              <p>Seamlessly integrates with existing ESG reporting processes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section style={{padding: '80px 20px', background: 'white'}}>
        <div className="container" style={{maxWidth: '1200px', margin: '0 auto'}}>
          <img 
            src="https://customer-assets.emergentagent.com/job_esg-marketplace/artifacts/hsbmuqng_308-name7079-jj.avif" 
            alt="Community Impact"
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}
          />
        </div>
      </section>

      {/* Contact Form */}
      <section className="cta-section" id="contact">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Create Measurable Impact?</h2>
            <p>Let's discuss how PlayBack Token can transform your ESG strategy</p>
            <form className="email-form" id="emailForm" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '600px', margin: '0 auto' }}>
              <input type="text" name="company_name" placeholder="Your company name" required />
              <input type="text" name="contact_name" placeholder="Your name" required />
              <input type="text" name="role" placeholder="Your role" required />
              <input type="email" name="email" placeholder="Your email address" required />
              <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%' }}>
                {loading ? 'Sending...' : 'Get in Touch'}
              </button>
            </form>
            <p className="privacy-note">We'll respond within 24 hours to discuss your needs</p>
          </div>
        </div>
      </section>

      {/* FAQ CTA */}
      <section className="faq-cta-section">
        <div className="container">
          <div className="faq-cta-content">
            <h2>Have Questions?</h2>
            <p>Find answers to common questions about how PlayBack Token works for companies</p>
            <Link to="/companies-faq" className="btn btn-large btn-outline">View Companies FAQ</Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default CompaniesPage;
